import "isomorphic-fetch";
import { ITeamChannelDocumentLibrary } from "../../src/taskpane/interfaces/services/ITeamChannelDocumentLibrary";
import { logger } from "../../utilities/logger";
import { getAuthenticatedClient } from "../../src/taskpane/services/GraphClientService";
import { SharePointSiteHelper } from "../../utilities/SharePointSiteHelper";
import { ISiteInfo, filterNonTopLevelSites } from "../../src/taskpane/interfaces/ISiteInfo";
import { getTeamPrimaryChannel } from "../../src/taskpane/services/GraphService";
import { getTessAccountProjects, ITessDossier, ITessProject, ITessProjectFolders, resolveTessDossiers, TessProjectStatusEnum, TessProjectStatuses } from "../../src/taskpane/services/TessService";
import { tryDecodeUriComponent } from "../../utilities/EncodeDecode";
import { SharePointFolderContentTypeIds } from "../../src/taskpane/config/constants";
import { mapDrivesToLibraryLocations } from "./clientGroupLibrarySources";

import { ILocation, LocationType } from "./locationTypes";
import { mapGraphSiteToLocation, mapGraphSubsiteToLocation } from "./siteLocationMapping";

export { LocationType };
export type { ILocation };

export interface ITeamChannelLocation extends ILocation {
    email: string;
    isPrivate: boolean;
    isShared: boolean;
    isPrimary: boolean;
    driveInfo?: ITeamChannelDocumentLibrary;
}

export interface ISharepointListLocation extends ILocation {
    rootItemCount?: number;
    itemCount?: number;
    sharePointListId: string;
    contentTypeId?: string;
    siteUrl?: string;
    /** Set on a library of ANOTHER site shown here (a client-group site in All documents):
     * the site's Graph id and name, so the library opens, validates and labels as that site's. */
    siteId?: string;
    siteDisplayName?: string;
    isCalculatingRootItemCount?: boolean;
}

export interface ISharepointFolderLocation extends ISharepointListLocation {
    driveId: string;
    path: string;
    siteId: string;
    siteUrl: string;
    sharePointUniqueId: string;
}

export interface ITessProjectLocation extends ITessProject, ILocation {
    status: TessProjectStatusEnum;
}

export interface ITessProjectContent {
    libraries: ISharepointListLocation[];
    folders: ISharepointFolderLocation[];
    projects?: ITessProjectLocation[];
}

export const getLocationSubSites = async (accessToken: string, siteId: string, allowedSites: string[], isHubSitesRequest: boolean = false): Promise<ILocation[]> => {
    try {
        const client = getAuthenticatedClient(accessToken);

        const requestPath: string = isHubSitesRequest
            ? `/sites?search=(contentclass:STS_Site OR contentclass:STS_Web) AND (DepartmentId:{${siteId}} OR DepartmentId:${siteId} OR (IsHubSite:true AND RelatedHubSites:${siteId})) -SiteId:${siteId}`
            : `/sites/${siteId}/sites`;

        let result = await client.api(requestPath).select("id,displayName,description,createdDateTime,lastModifiedDateTime,webUrl,root").get();
        let subSites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];

        let associatedHubSites: ISiteInfo[];
        let associatedHubSitesAvailable: boolean;
        if (siteId && isHubSitesRequest && SharePointSiteHelper.hubSites?.length > 0) {
            associatedHubSites = SharePointSiteHelper.hubSites.filter(hub => hub.parentHubId && hub.parentHubId.toLowerCase() === siteId.toLowerCase());
            associatedHubSitesAvailable = Array.isArray(associatedHubSites) && associatedHubSites.length > 0;
        }

        if (subSites?.length > 0 || associatedHubSitesAvailable) {
            let nextLink: string | null;
            if (result) nextLink = result["@odata.nextLink"] ?? null;
            if (nextLink?.length > 0) {
                try {
                    let subSiteCount: number = subSites.length;
                    let pageCount: number = 0;
                    while (nextLink && pageCount++ < 10) {
                        result = await client.api(nextLink).get();
                        const extraSites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];
                        const extraSiteCount: number = extraSites.length;
                        for (let i = 0; i < extraSiteCount; i++) {
                            subSites[subSiteCount++] = extraSites[i];
                        }
                        nextLink = result ? result["@odata.nextLink"] : null;
                    }
                } catch (error) {
                    logger(error, "LocationService.ts (getLocationSubSites: paging loop)");
                }
            }

            if (allowedSites?.length > 0 && subSites) {
                subSites = subSites.filter(subSite => allowedSites.some(allowedSite => subSite.displayName?.trim().toLowerCase() === allowedSite.trim()));
            }

            if (!Array.isArray(subSites)) subSites = [];

            if (isHubSitesRequest && SharePointSiteHelper.hubSites?.length > 0) {
                if (subSites.length > 0) {
                    subSites.forEach(subSite => {
                        const hub: ISiteInfo = SharePointSiteHelper.hubSites.find(hubSite => hubSite.id === subSite.id);
                        if (hub) {
                            subSite.isHub = true;
                            subSite.hubId = hub.hubId;
                            if (hub.name) subSite.displayName = hub.name;
                            if (hub.description) subSite.description = hub.description;
                            if (hub.parentHubId) subSite.parentHubId = hub.parentHubId;
                        }
                    });
                }

                if (associatedHubSitesAvailable) {
                    associatedHubSites.forEach(associatedHub => {
                        const hubExists: boolean = subSites.some(subSite => subSite.id?.trim().toLowerCase() === associatedHub.id?.trim().toLowerCase());
                        if (!hubExists) {
                            subSites.push({
                                ...associatedHub,
                                displayName: associatedHub.name
                            });
                        }
                    });
                }
            }

            return subSites
                .filter(subSite => !subSite.parentHubId || subSite.parentHubId === siteId)
                .filter(filterNonTopLevelSites)
                .map(isHubSitesRequest ? mapGraphSiteToLocation : mapGraphSubsiteToLocation)
                .sort((a, b) => a.displayName?.toUpperCase() < b.displayName?.toUpperCase() ? -1 : 1);
        }
    } catch (error) {
        logger(error, "LocationService.ts (getLocationSubSites)");
    }
    return [];
};

export const getLocationTeamChannels = async (accessToken: string, groupId: string): Promise<ITeamChannelLocation[]> => {
    if (groupId) {
        try {
            const client = getAuthenticatedClient(accessToken);
            // const teamChannels = (await client.api(`/teams/${groupId}/channels`).header("Prefer", "include-unknown-enum-members").get())?.value;
            const [teamChannels, primaryChannel] = await Promise.all([
                client.api(`/teams/${groupId}/channels`).header("Prefer", "include-unknown-enum-members").get(),
                getTeamPrimaryChannel(accessToken, groupId)
            ]);

            if (Array.isArray(teamChannels?.value)) {
                const channels: ITeamChannelLocation[] = teamChannels.value.map((teamChannel, index) => {
                    const createdDate = new Date(teamChannel.createdDateTime);
                    return <ITeamChannelLocation>{
                        key: `c${index}`,
                        id: teamChannel.id,
                        displayName: teamChannel.displayName,
                        description: teamChannel.description,
                        createdDateTime: createdDate.toLocaleDateString(navigator.languages),
                        createdDateTimeValue: createdDate.valueOf(),
                        lastModifiedDateTime: createdDate.toLocaleDateString(navigator.languages),
                        lastModifiedDateTimeValue: createdDate.valueOf(),
                        webUrl: teamChannel.webUrl,
                        email: teamChannel.email,
                        isPrivate: teamChannel.membershipType === "private",
                        isShared: teamChannel.membershipType === "shared",
                        isPrimary: teamChannel.id === primaryChannel?.id,
                        type: LocationType.TeamChannel
                    }
                });

                return [
                    ...channels.filter(x => x.isPrimary),
                    ...channels
                        .filter(x => !x.isPrimary)
                        .sort((channelA, channelB) => channelA.displayName?.toUpperCase() < channelB.displayName?.toUpperCase() ? -1 : 1)
                ];
            }
        } catch (error) {
            logger(error, "LocationService.ts (getLocationTeamChannels)");
        }
    }
    return [];
};

export const getLocationDocumentLibraries = async (accessToken: string, siteId: string): Promise<ISharepointListLocation[]> => {
    try {
        const client = getAuthenticatedClient(accessToken);
        const documentLibraries = (
            await client.api(`/sites/${siteId}/drives`)
                .select("id,name,description,createdDateTime,lastModifiedDateTime,webUrl,sharepointIds")
                .expand("list($select=displayName)")
                .get()
        )?.value;

        if (documentLibraries) {
            // Shared with the client-group sites the All documents view reads, so both skip
            // the same built-in system libraries (matched by url segment, not the localized
            // display name - see systemLibraryHelper) and build the same rows.
            return mapDrivesToLibraryLocations(documentLibraries);
        }
    } catch (error) {
        logger(error, "LocationService.ts (getLocationDocumentLibraries)");
    }
    return [];
};

export const getLocationSharepointLists = async (accessToken: string, siteId: string): Promise<ISharepointListLocation[]> => {
    try {
        const client = getAuthenticatedClient(accessToken);
        const sharepointLists = (await client.api(`/sites/${siteId}/lists`).select("id,name,displayName,description,createdDateTime,lastModifiedDateTime,webUrl,sharepointIds,list").get())?.value;

        if (sharepointLists) {
            return sharepointLists.filter(x => (x.list.template === "genericList" || x.list.template === "customGrid") && !x.list.hidden).map((spListItem, index) => {
                const createdDate = new Date(spListItem.createdDateTime);
                const modifiedDate = new Date(spListItem.lastModifiedDateTime);
                return <ISharepointListLocation>{
                    key: `l${index}`,
                    id: spListItem.id,
                    displayName: spListItem.displayName ?? spListItem.name,
                    description: spListItem.description,
                    createdDateTime: createdDate.toLocaleDateString(navigator.languages),
                    createdDateTimeValue: createdDate.valueOf(),
                    lastModifiedDateTime: modifiedDate.toLocaleDateString(navigator.languages),
                    lastModifiedDateTimeValue: modifiedDate.valueOf(),
                    webUrl: spListItem.webUrl,
                    type: LocationType.SharepointList,
                    sharePointListId: spListItem.sharepointIds.listId
                }
            })
                .sort((a, b) => a.displayName?.toUpperCase() < b.displayName?.toUpperCase() ? -1 : 1);
        }
    } catch (error) {
        logger(error, "LocationService.ts (getLocationSharpointLists)");
    }
    return [];
};

export const getLocationTessProjects = async (accessToken: string, tessAccountId: string): Promise<ITessProjectContent> => {
    try {
        let projectContent: ITessProjectContent = {
            libraries: [],
            folders: [],
            projects: []
        }

        const projectFolders: ITessProjectFolders = await getTessAccountProjects(tessAccountId);
        if (projectFolders) {
            if (projectFolders.account?.dossierMetadata?.length > 0) {
                projectContent = await getTessDossierData(accessToken, projectFolders.account.dossierMetadata);
            }

            const allProjects: ITessProjectLocation[] = [];
            await Promise.all(TessProjectStatuses.map(async (status) => {
                const projectSites = (await Promise.all(projectFolders[status.key].map(async (project, index) => {
                    if (Array.isArray(project.dossierMetadata) && project.dossierMetadata.length > 0) {
                        // const siteHit: IHit = await getSharePointSiteFromDriveId(project.dossierMetadata[0].driveId);
                        // if (siteHit) {
                        //     const site = siteHit.resource;
                        //     const createdDate = new Date(site.createdDateTime);
                        //     const modifiedDate = new Date(site.lastModifiedDateTime);
                        //     return <ITessProjectLocation>{
                        //         key: `ps_${status.key.toString()}_${index}`,
                        //         id: site.id,
                        //         displayName: project.name,
                        //         description: site.description,
                        //         createdDateTime: createdDate ? createdDate.toLocaleDateString() : "-",
                        //         createdDateTimeValue: createdDate ? createdDate.valueOf() : 0,
                        //         lastModifiedDateTime: modifiedDate ? modifiedDate.toLocaleDateString() : "-",
                        //         lastModifiedDateTimeValue: modifiedDate ? modifiedDate.valueOf() : 0,
                        //         webUrl: site.webUrl,
                        //         type: LocationType.SubSite,
                        //         status: status.key,
                        //         tessProjectId: project.id
                        //     }
                        // }
                        return <ITessProjectLocation>{
                            ...project,
                            key: `ps_${status.key.toString()}_${index}`,
                            id: project.id,
                            displayName: project.name,
                            type: LocationType.TessProject,
                            status: status.key,
                            tessProjectId: project.id
                        }
                    }
                    return null;
                })))
                    .filter(x => x);

                allProjects.push(...projectSites);
            }));

            projectContent.projects = allProjects.sort((a, b) => a.displayName?.toUpperCase() < b.displayName?.toUpperCase() ? -1 : 1);
        }

        return projectContent;
    } catch (error) {
        logger(error, "LocationService.ts (getLocationTessProjects)");
    }

    return null;
};

export const getLocationTessProjectLibrariesAndFolders = async (accessToken: string, tessAccountId: string, tessProjectId: string): Promise<ITessProjectContent> => {
    try {
        const projects: ITessProjectFolders = await getTessAccountProjects(tessAccountId);
        if (projects) {
            const project: ITessProject =
                projects.active.find(x => x.id === tessProjectId)
                || projects.drafts.find(x => x.id === tessProjectId)
                || projects.finished.find(x => x.id === tessProjectId);

            if (project?.dossierMetadata?.length > 0) {
                return await getTessDossierData(accessToken, project.dossierMetadata);
            }
        }
    } catch (error) {
        logger(error, "LocationService.ts (getLocationTessProjects)");
    }

    return { libraries: [], folders: [] };
}

const getTessDossierData = async (accessToken: string, dossierMetadata: ITessDossier[]): Promise<ITessProjectContent> => {
    const resolved = await resolveTessDossiers(accessToken, dossierMetadata);

    // Keys stay positional: two dossiers may legitimately target the same drive.
    const documentLibraries: ISharepointListLocation[] = resolved.libraries.map(({ displayName, drive }, index) => {
        const createdDate = new Date(drive.createdDateTime);
        const modifiedDate = new Date(drive.lastModifiedDateTime);
        return {
            key: `lib${index}`,
            id: drive.id,
            displayName,
            description: drive.description,
            createdDateTime: createdDate.toLocaleDateString(navigator.languages),
            createdDateTimeValue: createdDate.valueOf(),
            lastModifiedDateTime: modifiedDate.toLocaleDateString(navigator.languages),
            lastModifiedDateTimeValue: modifiedDate.valueOf(),
            webUrl: drive.webUrl,
            type: LocationType.DocumentLibrary,
            sharePointListId: drive.sharePointIds.listId,
            siteUrl: drive.sharePointIds.siteUrl
        };
    });

    const projectFolders: ISharepointFolderLocation[] = resolved.folders.map(({ dossier, item }, index) => {
        const createdDate = new Date(item.createdDateTime);
        const modifiedDate = new Date(item.lastModifiedDateTime);
        return {
            key: `fld${index}`,
            id: item.id,
            driveId: dossier.driveId,
            path: tryDecodeUriComponent(new URL(item.webDavUrl).pathname),
            displayName: dossier.name,
            description: item.description,
            createdDateTime: createdDate.toLocaleDateString(navigator.languages),
            createdDateTimeValue: createdDate.valueOf(),
            lastModifiedDateTime: modifiedDate.toLocaleDateString(navigator.languages),
            lastModifiedDateTimeValue: modifiedDate.valueOf(),
            webUrl: item.webUrl,
            siteId: item.sharepointIds.siteId,
            siteUrl: item.sharepointIds.siteUrl,
            type: LocationType.Folder,
            contentTypeId: SharePointFolderContentTypeIds.folder,
            sharePointListId: item.sharepointIds.listId,
            sharePointUniqueId: item.sharepointIds.listItemUniqueId,
        };
    });

    return { libraries: documentLibraries, folders: projectFolders };
}